import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Pill } from '../ui/pill'
import {
  CircuitBoard,
  Battery,
  Clock,
  Calendar,
} from 'lucide-react'
import { useDeviceStatus, useSystemStatus } from '../../stores/systemStore'
import { useConnectedDevice, useDeviceMonitoring } from '../../stores/deviceStore'

interface DeviceDetailModalProps {
  device: any
  deviceType: 'registered' | 'connected'
  isOpen: boolean
  onClose: () => void
}

export function DeviceDetailModal({ device, deviceType, isOpen, onClose }: DeviceDetailModalProps) {
  if (!device) return null

  const deviceStatus = useDeviceStatus()
  const systemStatus = useSystemStatus()
  const connectedDevice = useConnectedDevice()
  const deviceMonitoring = useDeviceMonitoring()

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDuration = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60))
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))
    return `${hours}h ${minutes}m`
  }

  const renderRegisteredDeviceInfo = () => {
    const isCurrentlyConnected = deviceStatus.isConnected && deviceStatus.currentDeviceId === device.id
    const connectionStatus = isCurrentlyConnected ? 'Connected' : 'Registered'

    return (
      <div className="space-y-6">
        <div>
          <h4 className="lb-h3 mb-3">Device Info</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium t-faint">Device Name</label>
                <p className="t-strong">{device.nickname || device.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium t-faint">Device ID</label>
                <p className="t-strong font-mono text-sm">{device.id}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium t-faint">Connection Status</label>
                <Pill size="sm" variant="secondary" className={isCurrentlyConnected ?
                  "bg-green-500/10 text-green-400 border-green-500/30" :
                  "bg-indigo-500/10 text-indigo-400 border-indigo-500/30"
                }>
                  {connectionStatus}
                </Pill>
              </div>
              {isCurrentlyConnected && (
                <>
                  <div>
                    <label className="text-sm font-medium t-faint">Connection Duration</label>
                    <p className="t-strong">{deviceMonitoring?.connectionDuration || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium t-faint">Battery Level</label>
                    <div className="flex items-center gap-2">
                      <Battery className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                      <span className="t-strong">{deviceMonitoring?.batteryLevel || 0}%</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-white/[0.06] pt-4">
          <h4 className="lb-h3 mb-3">Registration Information</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium t-faint">Registered Date</label>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <span className="t-strong">{formatDate(device.registeredAt)}</span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium t-faint">Last Connected</label>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <span className="t-strong">
                  {device.lastConnectedAt ? formatDate(device.lastConnectedAt) : 'Never'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/[0.06] pt-4">
          <h4 className="lb-h3 mb-3">Usage Statistics</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="text-center p-3 bg-white/[0.03] rounded-lg">
              <div className="lb-display-lg t-strong">{formatDuration(device.totalUsageDuration || 0)}</div>
              <div className="text-sm t-faint">Total Usage Duration</div>
            </div>
            <div className="text-center p-3 bg-white/[0.03] rounded-lg">
              <div className="lb-display-lg t-strong">{device.connectionCount || 0}</div>
              <div className="text-sm t-faint">Total Usage Time</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderConnectedDeviceInfo = () => (
    <div className="space-y-6">
      <div>
        <h4 className="lb-h3 mb-3">Device Info</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium t-faint">Device Name</label>
              <p className="t-strong">{connectedDevice?.device.name || deviceStatus.deviceName}</p>
            </div>
            <div>
              <label className="text-sm font-medium t-faint">Device ID</label>
              <p className="t-strong font-mono text-sm">{connectedDevice?.device.id || deviceStatus.currentDeviceId}</p>
            </div>
            <div>
              <label className="text-sm font-medium t-faint">Connection Start Time</label>
              <p className="t-strong">
                {connectedDevice?.connectionStartTime ?
                  new Date(connectedDevice.connectionStartTime).toLocaleTimeString() : 'N/A'}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium t-faint">Connection Status</label>
              <Pill size="sm" variant="secondary" className="bg-green-500/10 text-green-400 border-green-500/30">
                Connected
              </Pill>
            </div>
            <div>
              <label className="text-sm font-medium t-faint">Connection Duration</label>
              <p className="t-strong">{deviceMonitoring?.connectionDuration || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium t-faint">Battery Level</label>
              <div className="flex items-center gap-2">
                <Battery className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <span className="t-strong">{deviceMonitoring?.batteryLevel || deviceStatus.batteryLevel}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/[0.06] pt-4">
        <h4 className="lb-h3 mb-3">Sampling Rates</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-white/[0.03] rounded-lg">
            <div className="text-lg font-bold t-strong">{deviceMonitoring?.samplingRates?.eeg || 0}Hz</div>
            <div className="text-sm t-faint">EEG</div>
          </div>
          <div className="text-center p-3 bg-white/[0.03] rounded-lg">
            <div className="text-lg font-bold t-strong">{deviceMonitoring?.samplingRates?.ppg || 0}Hz</div>
            <div className="text-sm t-faint">PPG</div>
          </div>
          <div className="text-center p-3 bg-white/[0.03] rounded-lg">
            <div className="text-lg font-bold t-strong">{deviceMonitoring?.samplingRates?.acc || 0}Hz</div>
            <div className="text-sm t-faint">ACC</div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/[0.06] pt-4">
        <h4 className="lb-h3 mb-3">Signal Quality</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-white/[0.03] rounded-lg">
            <div className="text-lg font-bold t-strong">{deviceMonitoring?.signalQuality?.eeg || 0}%</div>
            <div className="text-sm t-faint">EEG</div>
          </div>
          <div className="text-center p-3 bg-white/[0.03] rounded-lg">
            <div className="text-lg font-bold t-strong">{deviceMonitoring?.signalQuality?.ppg || 0}%</div>
            <div className="text-sm t-faint">PPG</div>
          </div>
          <div className="text-center p-3 bg-white/[0.03] rounded-lg">
            <div className="text-lg font-bold t-strong">{deviceMonitoring?.signalQuality?.acc || 0}%</div>
            <div className="text-sm t-faint">ACC</div>
          </div>
          <div className="text-center p-3 bg-white/[0.03] rounded-lg">
            <div className="text-lg font-bold t-strong">{deviceMonitoring?.signalQuality?.overall || 0}%</div>
            <div className="text-sm t-faint">Overall</div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl mx-2 max-h-[90vh] sm:max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/[0.03] rounded-lg flex items-center justify-center">
              <CircuitBoard className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-lg font-semibold">{device.name}</div>
              <div className="text-sm t-faint">
                {deviceType === 'connected' ? 'Connected Device' : 'Registered Device'} Details
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-6">
          {deviceType === 'registered' ? renderRegisteredDeviceInfo() : renderConnectedDeviceInfo()}
        </div>

        <div className="flex justify-end pt-4 border-t border-white/[0.06]">
          <Pill variant="secondary" onClick={onClose}>
            Close
          </Pill>
        </div>
      </DialogContent>
    </Dialog>
  )
}
